import { Button, Modal, ModalStyle, modalWidth } from "@hubrise/react-components"
import * as React from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { FieldName } from "Catalog/Edit/OptionLists/Content/Options/Styles"
import { ToolButton } from "Catalog/shared/components/CatalogToolbar"
import useEditor from "shared/hooks/useEditor"
import { IVariant } from "shared/hubrise/models"
import * as Variant from "shared/hubrise/utils/Variant"
import { changeVariantRefPath } from "shared/utils/paths"

import { Intro, RefCode, Show, StyledTable } from "./Styles"

const ManageVariants = (): JSX.Element => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { catalog, editor } = useEditor()

  const $newRowNameRef = React.useRef<HTMLInputElement>(null)

  const variants = catalog.data.variants

  const handleChange =
    (variant: IVariant, updateForNew: ((variant: IVariant) => void) | undefined) => (name: string) =>
      updateForNew ? updateForNew({ ...variant, name }) : editor.variant.update(variant.id, name)

  const handleReorder = (variants: Array<IVariant>): void => {
    editor.variant.reorder(variants)
  }

  const handleDelete = (variant: IVariant): void => editor.variant.delete(variant)

  const getNew = (): IVariant => {
    setTimeout(() => $newRowNameRef.current!.focus(), 0)
    return Variant.create(catalog)
  }

  const tryCreate = (variant: IVariant): boolean => {
    if (variant.name.length > 0) {
      editor.variant.add(variant)
      return true
    } else {
      return false
    }
  }

  return (
    <Modal
      modalWidth={modalWidth.medium}
      renderLink={({ open }) => (
        <ToolButton icon="layers" onClick={open}>
          {t("variants.manage.button_label")}
        </ToolButton>
      )}
      renderContent={({ close }) => (
        <>
          <ModalStyle.Header>
            <ModalStyle.Title>
              {t("variants.manage.title", { catalogName: catalog.name, catalogId: catalog.hrId })}
            </ModalStyle.Title>
            <ModalStyle.Close onClick={close} />
          </ModalStyle.Header>

          <ModalStyle.Body>
            <Intro>{t("variants.manage.intro")}</Intro>

            <StyledTable
              items={variants}
              columns={[
                {
                  header: t("variants.name"),
                  cellRenderer: (variant, updateForNew) => (
                    <FieldName
                      ref={updateForNew ? $newRowNameRef : undefined}
                      initialValue={variant.name}
                      onChange={handleChange(variant, updateForNew)}
                      debounce={updateForNew === undefined}
                      validate={(name) => (name.length === 0 ? t("validations.not_empty") : undefined)}
                    />
                  ),
                },
                {
                  header: t("variants.ref"),
                  cellRenderer: (variant) => <RefCode>{variant.ref}</RefCode>,
                },
                {
                  cellRenderer: (variant) => (
                    <Show
                      onClick={() => {
                        close()
                        navigate(changeVariantRefPath(variant.ref))
                      }}
                    >
                      {t("variants.manage.show")}
                    </Show>
                  ),
                },
              ]}
              onReorder={handleReorder}
              onDelete={handleDelete}
              droppableId="variant"
              addBehavior={{ buttonLabel: t("variants.manage.add"), getNew, tryCreate }}
            />
          </ModalStyle.Body>

          <ModalStyle.Footer>
            <Button variant="empty" onClick={close}>
              {t("general.close")}
            </Button>
          </ModalStyle.Footer>
        </>
      )}
    />
  )
}

export default ManageVariants
